// src/pages/components/EmailDialog.js
import { useEffect, useState, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Autocomplete from "@mui/material/Autocomplete";

import MDButton from "components/MDButton";

import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";
import api, { handleApi } from "pages/api/api";
import { useSpeechRecognition } from "pages/components/useSpeechRecognition";

// ---------- utils ----------
const emailPattern = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@(?:[A-Za-z0-9-]+\.)+[A-Za-z]{2,}$/;

// divide "a@x.pt; b@y.pt" numa lista limpa
const splitEmails = (value) =>
  (value || "")
    // separa por ponto e vírgula, vírgula OU espaço (um ou mais)
    .split(/[;, ]+/)
    .map((v) => v.trim())
    .filter(Boolean);

const isValidEmail = (v) => emailPattern.test(String(v).trim());

// valida uma LISTA de e-mails (se allowEmpty for false, tem de ter pelo menos um)
const isValidEmailList = (value, { allowEmpty = false } = {}) => {
  const list = splitEmails(value);
  if (!list.length) return allowEmpty;
  return list.every((e) => isValidEmail(e));
};

const appendEmailToList = (current, email) => {
  if (!email) return current || "";

  const list = splitEmails(current);

  // se já existe, não duplica
  if (list.some((e) => e.toLowerCase() === String(email).toLowerCase())) {
    return current || email;
  }

  // Se lista vazia → devolve apenas o email
  if (!list.length) return email;

  // Limpa eventuais separadores ; , no final e múltiplos espaços
  const trimmed = current
    .trim()
    .replace(/[;,]+$/, "") // remove separadores no fim
    .replace(/\s+/g, " "); // normaliza espaços múltiplos

  return `${trimmed} ${email}`; // separador = espaço
};

const capFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const endsWithPunct = (s) => /[.!?…]$/.test((s || "").trim());
const appendParagraph = (base, txt) => {
  if (!txt) return base || "";
  const clean = txt.trim();
  const withCap = capFirst(clean);
  const withDot = endsWithPunct(withCap) ? withCap : `${withCap}.`;
  const needsNL = base && !base.endsWith("\n") ? "\n" : "";
  return `${base || ""}${needsNL}${withDot}\n`;
};
const fmtSize = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`;

// ---------- localStorage keys ----------
const LS_KEY_REPLYTO = "cv.replyTo";
const LS_KEY_REPLYTO_NAME = "cv.replyToName";
const LS_KEY_REMEMBER = "cv.rememberReplyTo";

export default function EmailDialog({
  open,
  onClose,
  // valor inicial opcional do campo "Para"
  to = "",
  // limites por defeito (seguros p/ Gmail/Outlook)
  maxEachMB = 18, // 18 MB por ficheiro
  maxTotalMB = 20, // 20 MB total
}) {
  const { showAlert } = useAlert();
  const { userData } = useAuth();

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const fullScreen = isXs;

  // remetente
  const [replyTo, setReplyTo] = useState(userData?.email || "");
  const [replyToName, setReplyToName] = useState("");

  const [remember, setRemember] = useState(true);

  // destino
  const [listaDestinatarios, setListaDestinatarios] = useState([]);
  const [loadingDest, setLoadingDest] = useState(false);
  const [selDest, setSelDest] = useState(null); // objeto da combo
  const [para, setPara] = useState(to || "");
  const [cc, setCc] = useState("");

  // assunto + mensagem
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const [sending, setSending] = useState(false);

  // anexos
  const [files, setFiles] = useState([]); // File[]
  const maxEach = useMemo(() => maxEachMB * 1024 * 1024, [maxEachMB]);
  const maxTotal = useMemo(() => maxTotalMB * 1024 * 1024, [maxTotalMB]);
  const totalSize = useMemo(() => files.reduce((s, f) => s + (f.size || 0), 0), [files]);

  // voz (one-shot)
  const { iniciar, parar, reconhecendo, suporteVoz, erro } = useSpeechRecognition((texto) => {
    setMessage((m) => appendParagraph(m, texto));
  });

  useEffect(() => {
    if (erro && erro !== "no-speech") {
      showAlert(`Falha no reconhecimento de voz: ${erro}`, "warning");
    }
  }, [erro, showAlert]);

  // carregar remetente + destinatários ao abrir
  useEffect(() => {
    const carregarDestinatarios = async () => {
      setLoadingDest(true);
      const [dados, err] = await handleApi(
        api.post("UtilizadoresAcessos/SelUtilizadoresEmailCombo", {})
      );
      setLoadingDest(false);
      if (err) {
        showAlert(err, "error");
        setListaDestinatarios([]);
        return;
      }
      setListaDestinatarios(Array.isArray(dados) ? dados : []);
    };

    if (open) {
      const rem = localStorage.getItem(LS_KEY_REMEMBER) === "1";
      setRemember(rem);

      // E-mail do remetente vem SEMPRE do utilizador autenticado
      setReplyTo(userData?.email || "");

      // Nome do remetente: se estiver guardado e "lembrar" ativo, usa esse;
      // caso contrário, usa o nome do utilizador autenticado.
      const nomeGuardado = localStorage.getItem(LS_KEY_REPLYTO_NAME) || "";
      if (rem && nomeGuardado) {
        setReplyToName(nomeGuardado);
      } else {
        setReplyToName(userData?.designacao || "");
      }

      // reset de destino
      setPara(to || "");
      setCc("");
      setSelDest(null);

      carregarDestinatarios();
    } else {
      setFiles([]);
      setSubject("");
      setMessage("");
      setSelDest(null);
    }
  }, [open, showAlert, to, userData]);

  // ---------- anexos handlers ----------
  const onPickFiles = (event) => {
    const incoming = Array.from(event.target.files || []);
    if (!incoming.length) return;

    // valida tamanhos individuais
    for (const f of incoming) {
      if (f.size > maxEach) {
        showAlert(
          <>
            <strong>'{f.name}'</strong> excede {maxEachMB} MB. Sugestão: use um serviço gratuito
            (ex.:{" "}
            <a href="https://wetransfer.com" target="_blank" rel="noopener noreferrer">
              https://wetransfer.com
            </a>
            ) e escreva na mensagem apenas o link que ele gerar.
          </>,
          "warning",
          { duration: 10000, persist: true }
        );

        return;
      }
    }

    // valida tamanho total
    const incomingTotal = incoming.reduce((s, f) => s + (f.size || 0), 0);
    if (totalSize + incomingTotal > maxTotal) {
      showAlert(
        <>
          Tamanho total excede {maxTotalMB} MB. Sugestão: use um serviço gratuito (ex.:{" "}
          <a href="https://wetransfer.com" target="_blank" rel="noopener noreferrer">
            https://wetransfer.com
          </a>
          ) e escreva na mensagem apenas o link que ele gerar.
        </>,
        "warning",
        { duration: 10000, persist: true }
      );
      return;
    }

    setFiles((prev) => [...prev, ...incoming]);
    event.target.value = "";
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  // ---------- helpers destino ----------
  const handleCopiarPara = () => {
    if (selDest?.email) {
      setPara((prev) => appendEmailToList(prev, selDest.email));
    }
  };

  const handleCopiarCc = () => {
    if (selDest?.email) {
      setCc((prev) => appendEmailToList(prev, selDest.email));
    }
  };

  const toNameFromSel =
    selDest && selDest.email === (para || "").trim() ? selDest.designacao || null : null;

  const paraValido = isValidEmailList(para, { allowEmpty: false });
  const ccValido = isValidEmailList(cc, { allowEmpty: true });

  // ---------- enviar ----------
  const handleSend = async () => {
    if (reconhecendo) {
      parar();
      return showAlert("Pare a gravação antes de enviar.", "warning");
    }
    if (!subject.trim()) return showAlert("Indique um assunto.", "warning");
    if (!message.trim()) return showAlert("Escreva uma mensagem.", "warning");

    if (!para.trim() || !paraValido) {
      return showAlert("O campo 'Para' não está preenchido com um e-mail válido.", "warning");
    }
    if (!isValidEmail(replyTo)) {
      return showAlert("O e-mail do remetente não é válido.", "warning");
    }
    if (cc && !ccValido) {
      return showAlert("O campo 'Cc' não contém um e-mail válido.", "warning");
    }

    if (remember) {
      localStorage.setItem(LS_KEY_REMEMBER, "1");
      localStorage.setItem(LS_KEY_REPLYTO_NAME, replyToName || "");
    } else {
      localStorage.removeItem(LS_KEY_REMEMBER);
      localStorage.removeItem(LS_KEY_REPLYTO_NAME);
    }

    const fd = new FormData();
    fd.append("to", para.trim());
    if (toNameFromSel) fd.append("toName", toNameFromSel);
    fd.append("subject", subject.trim());
    fd.append("html", `<p>${escapeHtml(message.trim()).replace(/\n/g, "<br/>")}</p>`);
    fd.append("text", message.trim());

    if (replyTo) fd.append("replyTo", replyTo);
    if (replyToName) fd.append("replyToName", replyToName);
    if (cc.trim()) fd.append("cc", cc.trim());

    files.forEach((f) => fd.append("files", f, f.name));

    setSending(true);
    const [, err] = await handleApi(
      api.post("Administracao/emails/enviar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    );
    setSending(false);

    if (err) {
      showAlert(err, "error");
      return;
    } else {
      showAlert("Mensagem enviada. Obrigado!", "success");
    }

    onClose?.();
    setSubject("");
    setMessage("");
    setFiles([]);
  };

  const canSend =
    subject.trim().length > 0 &&
    message.trim().length > 0 &&
    paraValido &&
    ccValido &&
    isValidEmail(replyTo) &&
    totalSize <= maxTotal &&
    files.every((f) => f.size <= maxEach);

  const limitsText = `Limites: ${maxEachMB} MB por ficheiro / ${maxTotalMB} MB total.`;
  const summaryText = files.length
    ? `${files.length} ficheiro(s) — ${fmtSize(totalSize)}.`
    : "Sem anexos.";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle>Enviar mensagem</DialogTitle>

      <DialogContent dividers>
        {/* Remetente */}
        <TextField
          label="Remetente (responder para)"
          value={replyTo}
          fullWidth
          size="small"
          margin="dense"
          InputProps={{
            readOnly: true,
            style: { backgroundColor: "#f5f5f5", cursor: "not-allowed" },
          }}
        />

        <TextField
          label="Nome do remetente (opcional)"
          value={replyToName}
          onChange={(e) => setReplyToName(e.target.value)}
          fullWidth
          size="small"
          margin="dense"
        />

        <FormControlLabel
          sx={{ mt: 0.5 }}
          control={
            <Checkbox
              size="small"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
            />
          }
          label="Lembrar remetente neste dispositivo"
        />

        {/* Destinatário via combo */}
        <Stack
          direction={isXs ? "column" : "row"}
          spacing={1}
          alignItems={isXs ? "stretch" : "flex-end"}
          sx={{ mt: 2 }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: 12,
                color: "#7b809a",
                marginBottom: 4,
              }}
            >
              Utilizador
            </div>
            <Autocomplete
              size="small"
              loading={loadingDest}
              options={listaDestinatarios}
              getOptionLabel={(opt) =>
                opt?.designacao && opt?.email
                  ? `${opt.designacao} (${opt.email})`
                  : opt?.email || ""
              }
              value={selDest}
              onChange={(_, value) => setSelDest(value)}
              isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={loadingDest ? "A carregar..." : "Selecione o destinatário…"}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingDest ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </div>

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <MDButton
              variant="outlined"
              color="info"
              size="small"
              onClick={handleCopiarPara}
              disabled={!selDest?.email}
            >
              Para
            </MDButton>
            <MDButton
              variant="outlined"
              color="info"
              size="small"
              onClick={handleCopiarCc}
              disabled={!selDest?.email}
            >
              Cc
            </MDButton>
          </Stack>
        </Stack>

        {/* Campos Para / Cc */}
        <TextField
          label="Para"
          placeholder="destino@jfbebidas.com outro@jfbebidas.com"
          value={para}
          onChange={(e) => setPara(e.target.value)}
          type="email"
          fullWidth
          size="small"
          margin="dense"
          error={!!para && !paraValido}
          helperText={
            para && !paraValido
              ? "Algum endereço está inválido. Pode indicar vários e-mails, separados por espaços."
              : "Pode indicar vários e-mails, separados por espaços."
          }
        />

        <TextField
          label="Cc (opcional)"
          placeholder="copia@jfbebidas.com outra.copia@jfbebidas.com"
          value={cc}
          onChange={(e) => setCc(e.target.value)}
          type="email"
          fullWidth
          size="small"
          margin="dense"
          error={!!cc && !ccValido}
          helperText={
            cc && !ccValido
              ? "Algum endereço em Cc está inválido. Separe vários e-mails com espaços."
              : "Opcional. Pode indicar vários e-mails, separados por espaços."
          }
        />

        {/* Assunto + mensagem */}
        <TextField
          label="Assunto"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          fullWidth
          size="small"
          margin="dense"
        />

        <TextField
          label="Mensagem"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          fullWidth
          size="small"
          margin="dense"
          multiline
          minRows={5}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip
                  title={
                    suporteVoz
                      ? reconhecendo
                        ? "A gravar… clique para parar"
                        : "Ditado por voz (Chrome)"
                      : "Ditado não suportado neste browser"
                  }
                >
                  <span>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => (reconhecendo ? parar() : iniciar())}
                      disabled={!suporteVoz}
                      sx={{
                        color: reconhecendo ? "error.main" : "action.active",
                      }}
                    >
                      <Icon fontSize="small">{reconhecendo ? "mic_off" : "mic"}</Icon>
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />

        {/* Anexos – Alert responsivo */}
        <Stack
          direction={isXs ? "column" : "row"}
          spacing={1}
          alignItems={isXs ? "stretch" : "center"}
          sx={{ mt: 1 }}
        >
          <MDButton
            variant="outlined"
            color="info"
            size="small"
            component="label"
            startIcon={<Icon>attach_file</Icon>}
            fullWidth={isXs}
          >
            Anexar ficheiros
            <input
              type="file"
              hidden
              multiple
              onChange={onPickFiles}
              accept=".pdf,image/*,.txt,.doc,.docx"
            />
          </MDButton>

          <Alert
            severity="info"
            variant="outlined"
            sx={{
              flex: 1,
              py: 0.5,
              "& .MuiAlert-message": {
                width: "100%",
                overflowWrap: "anywhere",
              },
            }}
          >
            <strong>{summaryText}</strong> {limitsText}
          </Alert>
        </Stack>

        {files.length > 0 && (
          <List
            dense
            sx={{
              mt: 1,
              border: (t) => `1px dashed ${t.palette.divider}`,
              borderRadius: 1,
            }}
          >
            {files.map((f, idx) => (
              <ListItem key={`${f.name}-${idx}`} sx={{ py: 0.25 }}>
                <ListItemText
                  primary={f.name}
                  secondary={`${fmtSize(f.size)}${
                    f.size > maxEach ? ` — excede ${maxEachMB} MB` : ""
                  }`}
                  primaryTypographyProps={{ noWrap: true }}
                />
                <ListItemSecondaryAction>
                  <IconButton edge="end" size="small" onClick={() => removeFile(idx)}>
                    <Icon fontSize="small">close</Icon>
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <MDButton color="secondary" variant="outlined" onClick={onClose} disabled={sending}>
          Cancelar
        </MDButton>
        <MDButton
          onClick={handleSend}
          variant="gradient"
          color="info"
          disabled={!canSend || sending}
          endIcon={
            sending ? <CircularProgress size={16} sx={{ color: "white" }} /> : <Icon>send</Icon>
          }
        >
          {sending ? "A enviar..." : "Enviar"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

// escapar HTML simples
function escapeHtml(str) {
  return (str || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}
